import {onBeforeUnmount, reactive, ref} from 'vue';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import {Table, TableCell, TableHeader, TableRow} from '@tiptap/extension-table';
import Youtube from '@tiptap/extension-youtube';
import {Placeholder} from '@tiptap/extensions';
import type {EditorState} from '@tiptap/pm/state';
import type {EditorView} from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import {useEditor, VueNodeViewRenderer} from '@tiptap/vue-3';
import GlobalDragHandle from 'tiptap-extension-global-drag-handle';
import PortfolioEditorImageView from '~/features/portfolio/editor/ui/PortfolioEditorImageView.vue';
import {PortfolioHelper} from '~/helpers/portfolio/PortfolioHelper';

// Matches share URLs: vimeo.com/ID and vimeo.com/ID/hash (private videos)
const VIMEO_REGEX = /^https?:\/\/(?:www\.)?vimeo\.com\/\d+(?:\/\w+)?$/;

function handleVimeoPaste(view: import('@tiptap/pm/view').EditorView, event: ClipboardEvent): boolean {
    const text = event.clipboardData?.getData('text/plain')?.trim() ?? '';
    if (!VIMEO_REGEX.test(text)) return false;

    const node = view.state.schema.nodes.youtube?.create({src: text});
    if (!node) return false;

    view.dispatch(view.state.tr.replaceSelectionWith(node));
    return true;
}

const YoutubeWithVimeo = Youtube.extend({
    addCommands() {
        return {
            setYoutubeVideo:
                (options: {src: string; start?: number}) =>
                ({commands}) => {
                    if (!PortfolioHelper.getVideoType(options.src)) return false;
                    return commands.insertContent({type: this.name, attrs: options});
                },
        };
    },
    renderHTML({node, HTMLAttributes}) {
        if (PortfolioHelper.getVideoType(HTMLAttributes.src) === 'VIMEO') {
            return [
                'div',
                {'data-youtube-video': ''},
                [
                    'iframe',
                    {
                        src: PortfolioHelper.getEmbededVideoURL(HTMLAttributes.src),
                        allowfullscreen: 'true',
                        frameborder: '0',
                        width: this.options.width,
                        height: this.options.height,
                    },
                ],
            ];
        }
        return this.parent!({node, HTMLAttributes});
    },
});

type PortfolioEditorContent = Record<string, unknown>;

type UsePortfolioItemEditorOptions = {
    readonly?: boolean;
    content?: PortfolioEditorContent | null;
    onUpdateContent?: (content: PortfolioEditorContent | null) => void;
    placeholder: string;
};

export function usePortfolioItemEditor(options: UsePortfolioItemEditorOptions) {
    const isReadonly = options.readonly === true;
    const fileInputRef = ref<HTMLInputElement | null>(null);
    const videoUrl = ref('');

    const slashMenuState = reactive({visible: false, query: '', x: 0, y: 0, cursorTop: 0, slashFrom: 0});

    function hideSlashMenu() {
        slashMenuState.visible = false;
    }

    function deleteSlashText() {
        const currentPos = editor.value?.state.selection.from ?? 0;
        editor.value?.chain().deleteRange({from: slashMenuState.slashFrom, to: currentPos}).run();
    }

    function detectSlash(scope: {state: EditorState; view: EditorView}) {
        const view = scope.view;
        if (view.composing) {
            return;
        }
        const {from, empty} = scope.state.selection;
        if (!empty) {
            slashMenuState.visible = false;
            return;
        }
        const $from = scope.state.doc.resolve(from);
        const normalized = $from.parent.textBetween(0, $from.parentOffset).replace(/\u200b/g, '');
        const match = normalized.match(/(^|[\s\u00a0])\/([^\s]*)$/);
        if (match) {
            const query = match[2];
            slashMenuState.slashFrom = from - query.length - 1;
            const coords = view.coordsAtPos(from);
            slashMenuState.visible = true;
            slashMenuState.query = query;
            slashMenuState.x = coords.left;
            slashMenuState.y = coords.bottom + 4;
            slashMenuState.cursorTop = coords.top;
        } else {
            slashMenuState.visible = false;
        }
    }

    const editor = useEditor({
        extensions: [
            GlobalDragHandle.configure({dragHandleWidth: 22}),
            StarterKit,
            Image.extend({
                addAttributes() {
                    return {
                        ...this.parent?.(),
                        width: {
                            default: null,
                            parseHTML: (el) => (el.getAttribute('width') ? Number(el.getAttribute('width')) : null),
                            renderHTML: (attrs) => (attrs.width ? {width: attrs.width} : {}),
                        },
                        float: {
                            default: null,
                            parseHTML: (el) => el.getAttribute('data-float') ?? null,
                            renderHTML: (attrs) => (attrs.float ? {'data-float': attrs.float} : {}),
                        },
                    };
                },
                addNodeView() {
                    return VueNodeViewRenderer(PortfolioEditorImageView);
                },
            }),
            YoutubeWithVimeo,
            Table.configure({resizable: true}),
            TableRow,
            TableHeader,
            TableCell,
            Link.configure({openOnClick: isReadonly}),
            Placeholder.configure({placeholder: options.placeholder}),
        ] as any[],
        editable: !isReadonly,
        content: options.content ?? undefined,
        editorProps: {
            attributes: {
                class: 'editor__prosemirror',
            },
            handlePaste: handleVimeoPaste,
        },
        onUpdate: ({editor: e}) => {
            options.onUpdateContent?.(e.getJSON());
            if (!isReadonly) {
                detectSlash(e);
            }
        },
        onSelectionUpdate: ({editor: e}) => {
            if (!isReadonly) {
                detectSlash(e);
            }
        },
        onBlur: ({editor: ed, event}) => {
            const related = event.relatedTarget;
            queueMicrotask(() => {
                if (ed.isFocused) {
                    return;
                }
                if (related instanceof Node && typeof document !== 'undefined') {
                    const menu = document.querySelector('.slash-menu');
                    if (menu?.contains(related)) {
                        return;
                    }
                }
                hideSlashMenu();
            });
        },
    });

    function triggerImageUpload() {
        fileInputRef.value?.click();
    }

    function onImageFileSelected(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const src = e.target?.result as string;
            editor.value?.chain().focus().setImage({src}).run();
        };
        reader.readAsDataURL(file);

        (event.target as HTMLInputElement).value = '';
    }

    function insertVideo() {
        if (!videoUrl.value) return;
        editor.value?.chain().focus().setYoutubeVideo({src: videoUrl.value}).run();
    }

    function insertTable() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor.value?.chain().focus() as any).insertTable({rows: 3, cols: 3, withHeaderRow: true}).run();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tableChain = () => editor.value?.chain().focus() as any;

    function addColumnBefore() {
        tableChain()?.addColumnBefore().run();
    }
    function addColumnAfter() {
        tableChain()?.addColumnAfter().run();
    }
    function deleteColumn() {
        tableChain()?.deleteColumn().run();
    }
    function addRowBefore() {
        tableChain()?.addRowBefore().run();
    }
    function addRowAfter() {
        tableChain()?.addRowAfter().run();
    }
    function deleteRow() {
        tableChain()?.deleteRow().run();
    }
    function deleteTable() {
        tableChain()?.deleteTable().run();
    }

    function insertBlockquote() {
        editor.value?.chain().focus().toggleBlockquote().run();
    }

    function insertDivider() {
        editor.value?.chain().focus().setHorizontalRule().run();
    }

    function insertHeading1() {
        editor.value?.chain().focus().toggleHeading({level: 1}).run();
    }

    function insertHeading2() {
        editor.value?.chain().focus().toggleHeading({level: 2}).run();
    }

    onBeforeUnmount(() => {
        editor.value?.destroy();
    });

    return {
        editor,
        fileInputRef,
        videoUrl,
        slashMenuState,
        hideSlashMenu,
        deleteSlashText,
        triggerImageUpload,
        onImageFileSelected,
        insertVideo,
        insertTable,
        addColumnBefore,
        addColumnAfter,
        deleteColumn,
        addRowBefore,
        addRowAfter,
        deleteRow,
        deleteTable,
        insertBlockquote,
        insertDivider,
        insertHeading1,
        insertHeading2,
    };
}
